import React, { useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Card, CardBody, Row, Col } from "reactstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useGetDealerSiteDailyStatSummary } from "api/dealer-site-daily-stats/service";
import { resolveDealerSitePublicHost } from "api/dealer-site/api";
import { getActiveColorStyle } from "utils/colorUtils";
import SubHeader from "components/Navbars/SubHeader";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const VEHICLE_COLORS = ["#2563eb", "#22c55e", "#38bdf8", "#f97316", "#94a3b8"];
const MOBILE_COLOR = "#2563eb";
const DESKTOP_COLOR = "#f97316";

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatCount = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(asNumber(value));

const formatPct = (value) => `${asNumber(value).toFixed(1)}%`;

const percentOf = (part, total) => (total > 0 ? (asNumber(part) / total) * 100 : 0);

const formatDayLabel = (dateKey) => {
  const parts = String(dateKey || "").split("-");
  if (parts.length !== 3) return dateKey || "";
  return `${parts[1]}/${parts[2]}`;
};

const HoverTip = ({ x, y, color, label, pct, count }) => (
  <div className="website-performance-tip" style={{ left: x, top: y }}>
    <span className="website-performance-tip__swatch" style={{ background: color }} />
    <span className="website-performance-tip__label">{label}</span>
    <em>{pct}</em>
    <strong>{count}</strong>
  </div>
);

const SegmentedBar = ({ segments }) => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const total = segments.reduce((sum, item) => sum + asNumber(item.value), 0);
  const ticks = 96;

  const tickItems = useMemo(() => {
    const items = Array.from({ length: ticks }).map((_, index) => {
      const ratio = (index + 0.5) / ticks;
      let cursor = 0;
      let segment = segments[segments.length - 1] || null;
      for (const item of segments) {
        cursor += total > 0 ? asNumber(item.value) / total : 0;
        if (ratio <= cursor) {
          segment = item;
          break;
        }
      }
      return {
        index,
        color: total > 0 ? segment?.color || "#94a3b8" : "rgba(148,163,184,0.25)",
        segmentKey: total > 0 ? String(segment?.key || "") : "",
      };
    });
    const counts = {};
    items.forEach((item) => {
      if (!item.segmentKey) return;
      counts[item.segmentKey] = (counts[item.segmentKey] || 0) + 1;
    });
    const seen = {};
    return items.map((item) => {
      const waveCount = counts[item.segmentKey] || 1;
      const waveIndex = seen[item.segmentKey] || 0;
      if (item.segmentKey) seen[item.segmentKey] = waveIndex + 1;
      const waveMs = (waveIndex / Math.max(waveCount - 1, 1)) * 160;
      return { ...item, waveDelay: `${waveMs.toFixed(0)}ms` };
    });
  }, [segments, total]);

  const updateHover = (event) => {
    const key = event.target?.getAttribute?.("data-segment");
    const segment = key ? segments.find((item) => String(item.key) === key) : null;
    if (!segment || !wrapRef.current) {
      setHover(null);
      return;
    }
    const rect = wrapRef.current.getBoundingClientRect();
    setHover({
      key: String(segment.key),
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      label: segment.label,
      color: segment.color,
      pct: formatPct(percentOf(segment.value, total)),
      count: formatCount(segment.value),
    });
  };

  return (
    <div
      ref={wrapRef}
      className={classNames("website-performance-bar", { "is-hovering": Boolean(hover) })}
      onMouseMove={updateHover}
      onMouseLeave={() => setHover(null)}
    >
      <div className="website-performance-bar__track">
        {tickItems.map((tick) => (
          <span
            key={tick.index}
            className={classNames("website-performance-bar__tick", {
              "is-active": hover?.key && hover.key === tick.segmentKey,
            })}
            data-segment={tick.segmentKey || undefined}
            style={{ background: tick.color, "--wave-delay": tick.waveDelay }}
          />
        ))}
      </div>
      <div className="website-performance-bar__labels">
        {segments.map((segment, index) => {
          const start = segments
            .slice(0, index)
            .reduce((sum, item) => sum + percentOf(item.value, total), 0);
          const width = percentOf(segment.value, total);
          if (width <= 0) return null;
          const isLast = index === segments.length - 1;
          const end = start + width;
          return (
            <span
              key={segment.key}
              className={classNames("website-performance-bar__marker", {
                "is-last": isLast,
                "is-active": hover?.key === String(segment.key),
              })}
              style={{ left: `${end}%` }}
            >
              <span className="website-performance-bar__hairline" />
              <em>{formatPct(width)}</em>
            </span>
          );
        })}
      </div>
      {hover ? (
        <HoverTip
          x={hover.x}
          y={hover.y}
          color={hover.color}
          label={hover.label}
          pct={hover.pct}
          count={hover.count}
        />
      ) : null}
    </div>
  );
};

const StatList = ({ items }) => (
  <div className="website-performance-list">
    {items.map((item) => {
      const inner = (
        <>
          <span className="website-performance-list__swatch" style={{ background: item.color }} />
          <span className="website-performance-list__label">{item.label}</span>
          {item.badge != null ? (
            <span className="website-performance-list__badge">{item.badge}</span>
          ) : null}
          {item.trend ? (
            <span className={`website-performance-trend__icon ${item.trend === "down" ? "is-down" : "is-up"}`}>
              {item.trend === "down" ? <MdArrowDownward size={11} /> : <MdArrowUpward size={11} />}
            </span>
          ) : null}
          <span className="website-performance-list__value">{item.display}</span>
        </>
      );

      if (item.href) {
        return (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="website-performance-list__row is-link"
          >
            {inner}
          </a>
        );
      }

      return (
        <div key={item.key} className="website-performance-list__row">
          {inner}
        </div>
      );
    })}
  </div>
);

const hexNoise = (col, row) => ((col * 17 + row * 31 + 7) % 100) / 100;

const DeviceHoneycomb = ({
  mobile = 0,
  desktop = 0,
  isLightTheme = false,
  mobileLabel,
  desktopLabel,
}) => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const cols = 22;
  const rows = 11;
  const radius = 7.2;
  const dx = Math.sqrt(3) * radius;
  const dy = 1.5 * radius;
  const width = dx * (cols + 0.5) + radius;
  const height = dy * rows + radius;
  const total = asNumber(mobile) + asNumber(desktop);
  const mobileShare = total > 0 ? asNumber(mobile) / total : 0.5;
  const emptyFill = isLightTheme ? "#eceff3" : "rgba(148,163,184,0.22)";
  const gapStroke = isLightTheme ? "#ffffff" : "#1e1e25";

  const cells = useMemo(() => {
    const next = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const nx = (col + (row % 2 ? 0.5 : 0)) / Math.max(cols - 1, 1) - 0.5;
        const ny = row / Math.max(rows - 1, 1) - 0.5;
        const dist = Math.sqrt(nx * nx * 1.05 + ny * ny * 1.55);
        const noise = hexNoise(col, row);
        const filled = total > 0 && dist < 0.46 + noise * 0.07;
        const split = nx - (mobileShare - 0.5) * 1.15;
        const isMobile = split + (noise - 0.5) * 0.12 < 0;
        const x = radius + col * dx + (row % 2 ? dx / 2 : 0);
        const y = radius + row * dy;
        const kind = filled ? (isMobile ? "mobile" : "desktop") : "empty";
        next.push({
          key: `${col}-${row}`,
          x,
          y,
          kind,
          fill: kind === "mobile" ? MOBILE_COLOR : kind === "desktop" ? DESKTOP_COLOR : emptyFill,
        });
      }
    }
    return next;
  }, [desktop, emptyFill, mobile, mobileShare, total]);

  const points = (cx, cy) =>
    Array.from({ length: 6 }, (_, i) => {
      const angle = (Math.PI / 180) * (60 * i - 30);
      return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
    }).join(" ");

  const updateHover = (event) => {
    const kind = event.target?.getAttribute?.("data-kind");
    if (kind !== "mobile" && kind !== "desktop") {
      setHover(null);
      return;
    }
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({
      kind,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const tip =
    hover?.kind === "mobile"
      ? {
          label: mobileLabel,
          pct: formatPct(percentOf(mobile, total)),
          count: formatCount(mobile),
          color: MOBILE_COLOR,
        }
      : hover?.kind === "desktop"
        ? {
            label: desktopLabel,
            pct: formatPct(percentOf(desktop, total)),
            count: formatCount(desktop),
            color: DESKTOP_COLOR,
          }
        : null;

  return (
    <div
      ref={wrapRef}
      className={classNames("website-performance-honeycomb", {
        "is-hover-mobile": hover?.kind === "mobile",
        "is-hover-desktop": hover?.kind === "desktop",
      })}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-hidden="true"
        onMouseMove={updateHover}
        onMouseLeave={() => setHover(null)}
      >
        {cells.map((cell) => (
          <polygon
            key={cell.key}
            className={cell.kind === "empty" ? "is-empty" : `is-device is-${cell.kind}`}
            data-kind={cell.kind}
            points={points(cell.x, cell.y)}
            fill={cell.fill}
            stroke={gapStroke}
            strokeWidth="2.5"
            strokeLinejoin="miter"
          />
        ))}
      </svg>
      {tip ? (
        <HoverTip x={hover.x} y={hover.y} color={tip.color} label={tip.label} pct={tip.pct} count={tip.count} />
      ) : null}
    </div>
  );
};

const WebsitePerformance = ({
  activeColor = "blue",
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const isTheme2 = shellThemeVariant === "2";
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const { data, isLoading } = useGetDealerSiteDailyStatSummary({ days: 30 });

  const summary = data?.series ? data : data?.data || {};
  const series = Array.isArray(summary.series) ? summary.series : [];
  const vehicles = Array.isArray(summary.vehicles) ? summary.vehicles : [];
  const last30 = summary.last30 || {};
  const last7 = summary.last7 || {};
  const today = summary.today || {};

  const vehicleSegments = useMemo(() => {
    const top = vehicles.slice(0, 4);
    const restViews = vehicles.slice(4).reduce((sum, item) => sum + asNumber(item.views), 0);
    const items = top.map((item, index) => ({
      key: item.id || String(index),
      label: item.stock || String(item.id || "").slice(0, 8) || t("websitePerformance.vehicleFallback"),
      value: asNumber(item.views),
      unique: asNumber(item.unique_visitors),
      color: VEHICLE_COLORS[index],
    }));
    if (restViews > 0) {
      items.push({
        key: "other",
        label: t("websitePerformance.otherVehicles"),
        value: restViews,
        unique: vehicles.slice(4).reduce((sum, item) => sum + asNumber(item.unique_visitors), 0),
        color: VEHICLE_COLORS[4],
      });
    }
    return items;
  }, [t, vehicles]);

  const totalVehicleViews = vehicleSegments.reduce((sum, item) => sum + item.value, 0);
  const publicHost = resolveDealerSitePublicHost(summary.site);
  const vehicleDetailUrl = (documentId) => {
    const id = String(documentId || "").trim();
    if (!publicHost || !id || id === "other") return null;
    return `https://${publicHost}/vehicle-details/${encodeURIComponent(id)}`;
  };
  const mobile = asNumber(last30.mobile);
  const desktop = asNumber(last30.desktop);
  const deviceTotal = mobile + desktop;
  const previous7 = useMemo(() => {
    const slice = series.slice(-14, -7);
    return {
      pageviews: slice.reduce((sum, row) => sum + asNumber(row.pageviews), 0),
      unique_visitors: slice.reduce((sum, row) => sum + asNumber(row.unique_visitors), 0),
      mobile: slice.reduce((sum, row) => sum + asNumber(row.mobile), 0),
      desktop: slice.reduce((sum, row) => sum + asNumber(row.desktop), 0),
    };
  }, [series]);

  const lineData = useMemo(
    () => ({
      labels: series.map((row) => formatDayLabel(row.date)),
      datasets: [
        {
          label: t("websitePerformance.pageviews"),
          data: series.map((row) => asNumber(row.pageviews)),
          borderColor: "#f97316",
          backgroundColor: "rgba(249, 115, 22, 0.12)",
          pointBackgroundColor: "#f97316",
          pointBorderColor: "#f97316",
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          tension: 0.25,
          fill: false,
        },
        {
          label: t("websitePerformance.uniqueVisitors"),
          data: series.map((row) => asNumber(row.unique_visitors)),
          borderColor: "rgba(148, 163, 184, 0.9)",
          backgroundColor: "transparent",
          pointBackgroundColor: "rgba(148, 163, 184, 0.9)",
          pointBorderColor: "rgba(148, 163, 184, 0.9)",
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          tension: 0.25,
          fill: false,
        },
      ],
    }),
    [series, t]
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: isTheme2 ? "#0f172a" : "#1e1e2f",
          titleColor: "#f1f5f9",
          bodyColor: "#cbd5e1",
          borderColor: "#1e293b",
          borderWidth: 1,
          displayColors: true,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.72)",
            font: { size: 10 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: isLightTheme ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
            drawBorder: false,
          },
          ticks: {
            color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.72)",
            font: { size: 10 },
            precision: 0,
          },
        },
      },
    }),
    [isTheme2, isLightTheme]
  );

  return (
    <div
      className={classNames("content website-performance-page", {
        "website-performance-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
      })}
    >
      <style>
        {`
          .website-performance-page.content {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          .website-performance-page .row {
            margin-left: -5px;
            margin-right: -5px;
          }
          .website-performance-page .row > [class*="col-"] {
            padding-left: 5px;
            padding-right: 5px;
          }
          .website-performance-page .row + .row {
            margin-top: 10px;
          }
          .website-performance-page,
          .website-performance-page .card,
          .website-performance-page .card-header,
          .website-performance-page .card-body,
          .website-performance-page .btn,
          .website-performance-page .website-performance-bar__tick,
          .website-performance-page .website-performance-list__swatch,
          .website-performance-page .website-performance-list__badge,
          .website-performance-page .website-performance-trend__icon,
          .website-performance-page .website-performance-foot__cell {
            border-radius: 0 !important;
          }
          .website-performance-page .card {
            box-shadow: none;
            height: 100%;
            margin-bottom: 0;
          }
          .website-performance-equal > [class*="col-"] {
            display: flex;
          }
          .website-performance-equal .card {
            width: 100%;
            display: flex;
            flex-direction: column;
          }
          .website-performance-equal .card-body {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
          }
          .website-performance-page .card .card-body {
            padding: 15px !important;
          }
          .website-performance-honeycomb {
            position: relative;
            flex: 1 1 auto;
            min-height: 176px;
            margin: 12px 0 6px;
            display: flex;
            align-items: center;
            overflow: visible;
          }
          .website-performance-honeycomb svg {
            width: 100%;
            height: 176px;
            display: block;
            overflow: visible;
          }
          .website-performance-honeycomb polygon {
            transform-box: fill-box;
            transform-origin: center;
            transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
              filter 0.32s ease, opacity 0.32s ease;
          }
          .website-performance-honeycomb polygon.is-device {
            cursor: pointer;
          }
          .website-performance-honeycomb polygon.is-empty {
            pointer-events: none;
          }
          .website-performance-honeycomb.is-hover-mobile polygon.is-mobile,
          .website-performance-honeycomb.is-hover-desktop polygon.is-desktop {
            transform: scale(1.12);
            filter: brightness(1.08);
          }
          .website-performance-honeycomb.is-hover-mobile polygon.is-desktop,
          .website-performance-honeycomb.is-hover-desktop polygon.is-mobile {
            opacity: 0.82;
          }
          .website-performance-tip {
            position: absolute;
            z-index: 4;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 5px 8px;
            pointer-events: none;
            transform: translate(-50%, calc(-100% - 12px));
            background: #252836;
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
            font-size: 11px;
            line-height: 1;
            white-space: nowrap;
            color: #e2e8f0;
          }
          .white-content .website-performance-tip {
            background: #ffffff;
            border-color: #e5e7eb;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
            color: #334155;
          }
          .website-performance-tip__swatch {
            width: 8px;
            height: 8px;
            flex: 0 0 8px;
          }
          .website-performance-tip__label {
            font-weight: 500;
          }
          .website-performance-tip em {
            font-style: normal;
            font-weight: 600;
            padding: 2px 6px;
            background: rgba(148, 163, 184, 0.18);
            color: #cbd5e1;
          }
          .white-content .website-performance-tip em {
            background: #f1f5f9;
            color: #334155;
          }
          .website-performance-tip strong {
            font-weight: 700;
            color: #ffffff;
          }
          .white-content .website-performance-tip strong {
            color: #1e293b;
          }
          .website-performance-kicker {
            font-size: 13px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.82) !important;
            margin-bottom: 4px;
            text-transform: none;
            letter-spacing: 0;
            background: none !important;
          }
          .white-content .website-performance-kicker {
            color: #6c757d !important;
          }
          .website-performance-metric {
            font-size: 22px;
            font-weight: 700;
            line-height: 1.2;
            margin: 0;
            color: #ffffff;
          }
          .white-content .website-performance-metric {
            color: #1e293b;
          }
          .website-performance-trend {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-top: 4px;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7) !important;
          }
          .white-content .website-performance-trend {
            color: #6c757d !important;
          }
          .website-performance-trend__icon {
            width: 14px;
            height: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            flex: 0 0 14px;
          }
          .website-performance-trend.is-up .website-performance-trend__icon,
          .website-performance-trend__icon.is-up {
            background: #16a34a;
          }
          .website-performance-trend.is-down .website-performance-trend__icon,
          .website-performance-trend__icon.is-down {
            background: #dc2626;
          }
          .website-performance-bar {
            position: relative;
            margin-top: 10px;
            margin-bottom: 8px;
            padding-top: 10px;
            overflow: visible;
          }
          .website-performance-bar__track {
            display: flex;
            gap: 2px;
            height: 20px;
            align-items: flex-end;
            overflow: visible;
          }
          .website-performance-bar__tick {
            flex: 1 1 0;
            min-width: 1px;
            display: block;
            height: 20px;
            cursor: pointer;
            transform-origin: bottom center;
            transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1),
              filter 0.2s ease, opacity 0.16s ease;
            transition-delay: var(--wave-delay, 0s), var(--wave-delay, 0s), 0s;
          }
          .website-performance-bar__tick.is-active {
            transform: translateY(-7px) scaleY(1.22);
            filter: brightness(1.08);
          }
          .website-performance-bar.is-hovering .website-performance-bar__tick:not(.is-active) {
            opacity: 0.78;
          }
          .website-performance-bar__labels {
            position: relative;
            height: 42px;
            margin-top: 0;
            overflow: visible;
            pointer-events: none;
          }
          .website-performance-bar__marker {
            position: absolute;
            top: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            transform: translateX(-50%);
            pointer-events: none;
            width: auto;
            margin-left: 0;
          }
          .website-performance-bar__marker.is-last {
            transform: translateX(-100%);
            align-items: flex-end;
          }
          .website-performance-bar__hairline {
            display: block;
            width: 1px;
            height: 20px;
            background: rgba(255, 255, 255, 0.55);
            flex: 0 0 20px;
            font-style: normal;
          }
          .white-content .website-performance-bar__hairline {
            background: #64748b;
          }
          .website-performance-bar__marker em {
            font-style: normal;
            font-size: 11px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.78) !important;
            margin: 4px 0 0;
            line-height: 1;
            white-space: nowrap;
          }
          .white-content .website-performance-bar__marker em {
            color: #6c757d !important;
          }
          .website-performance-equal .website-performance-list,
          .website-performance-equal .website-performance-empty {
            margin-top: auto;
          }
          .website-performance-list__row {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 0;
            border-bottom: 1px solid rgba(255,255,255,0.08);
            font-size: 11px;
          }
          .website-performance-list__row.is-link {
            color: inherit;
            text-decoration: none;
            cursor: pointer;
          }
          .website-performance-list__row.is-link:hover .website-performance-list__label {
            color: #2563eb;
            text-decoration: underline;
          }
          .white-content .website-performance-list__row {
            border-bottom-color: #eee;
          }
          .website-performance-list__row:last-child {
            border-bottom: 0;
          }
          .website-performance-list__swatch {
            width: 8px;
            height: 8px;
            flex: 0 0 8px;
          }
          .website-performance-list__label {
            flex: 1;
            min-width: 0;
            color: rgba(255, 255, 255, 0.78) !important;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .white-content .website-performance-list__label {
            color: #6c757d !important;
          }
          .website-performance-list__badge {
            font-size: 11px;
            font-weight: 600;
            padding: 1px 6px;
            background: rgba(148,163,184,0.16);
            color: #cbd5e1;
          }
          .white-content .website-performance-list__badge {
            background: #f1f5f9;
            color: #334155;
          }
          .website-performance-list__value {
            font-weight: 700;
            min-width: 42px;
            text-align: right;
            color: #ffffff;
          }
          .white-content .website-performance-list__value {
            color: #1e293b;
          }
          .website-performance-legend {
            display: flex;
            gap: 14px;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7) !important;
          }
          .white-content .website-performance-legend {
            color: #6c757d !important;
          }
          .website-performance-legend span {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .website-performance-legend i {
            width: 14px;
            height: 2px;
            display: inline-block;
          }
          .website-performance-chart {
            height: 210px;
            margin-top: 10px;
          }
          .website-performance-foot {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin-top: 12px;
          }
          .website-performance-foot__cell {
            background: rgba(148,163,184,0.08);
            padding: 8px 10px;
          }
          .website-performance-foot__cell label {
            display: block;
            font-size: 11px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.7) !important;
            margin-bottom: 2px;
          }
          .white-content .website-performance-foot__cell label {
            color: #6c757d !important;
          }
          .website-performance-foot__cell strong {
            font-size: 13px;
            font-weight: 700;
            color: #ffffff;
          }
          .white-content .website-performance-foot__cell strong {
            color: #1e293b;
          }
          .website-performance-empty {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7) !important;
            padding: 16px 0 0;
          }
          .white-content .website-performance-empty {
            color: #6c757d !important;
          }
        `}
      </style>

      <SubHeader
        activeColor={activeColor}
        title={t("routes.websitePerformance")}
        back={-1}
        backFallback="/inventory-website"
        compact
        theme2ScrollGapFill={isTheme2}
        isLightTheme={isLightTheme}
      />

      {isLoading ? (
        <Row>
          <Col md="12">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <div
                className="spinner-border"
                role="status"
                style={{ color: activeColorStyle.borderColor }}
              >
                <span className="sr-only">{t("websitePerformance.loading")}</span>
              </div>
            </div>
          </Col>
        </Row>
      ) : !summary.hasSite ? (
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <div className="website-performance-empty">{t("websitePerformance.noSite")}</div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between">
                    <div>
                      <div className="website-performance-kicker">{t("websitePerformance.dailyTraffic")}</div>
                      <p className="website-performance-metric">{formatCount(last30.pageviews)}</p>
                    </div>
                    <div className="website-performance-legend mt-2 mt-lg-0">
                      <span>
                        <i style={{ background: "rgba(148, 163, 184, 0.9)" }} />
                        {t("websitePerformance.uniqueVisitors")}
                      </span>
                      <span>
                        <i style={{ background: "#f97316" }} />
                        {t("websitePerformance.pageviews")}
                      </span>
                    </div>
                  </div>
                  <div className="website-performance-chart">
                    <Line data={lineData} options={lineOptions} />
                  </div>
                  <div className="website-performance-foot">
                    <div className="website-performance-foot__cell">
                      <label>{t("websitePerformance.today")}</label>
                      <strong>{formatCount(today.pageviews)}</strong>
                    </div>
                    <div className="website-performance-foot__cell">
                      <label>{t("websitePerformance.last7Days")}</label>
                      <strong>{formatCount(last7.pageviews)}</strong>
                    </div>
                    <div className="website-performance-foot__cell">
                      <label>{t("websitePerformance.last30Days")}</label>
                      <strong>{formatCount(last30.pageviews)}</strong>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="website-performance-equal">
            <Col lg="6">
              <Card>
                <CardBody>
                  <div className="website-performance-kicker">{t("websitePerformance.vehicleViews")}</div>
                  <p className="website-performance-metric">{formatCount(totalVehicleViews)}</p>
                  <div className="website-performance-trend">{t("websitePerformance.last30Days")}</div>
                  <SegmentedBar segments={vehicleSegments} />
                  {vehicleSegments.length === 0 ? (
                    <div className="website-performance-empty">{t("websitePerformance.noVehicleViews")}</div>
                  ) : (
                    <StatList
                      items={vehicleSegments.map((item) => ({
                        key: item.key,
                        color: item.color,
                        label: item.label,
                        display: formatCount(item.value),
                        href: vehicleDetailUrl(item.key),
                      }))}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6">
              <Card>
                <CardBody>
                  <div className="website-performance-kicker">{t("websitePerformance.devices")}</div>
                  <p className="website-performance-metric">
                    {deviceTotal > 0 ? formatPct(percentOf(mobile, deviceTotal)) : "0.0%"}
                  </p>
                  <div className="website-performance-trend">
                    {t("websitePerformance.mobileShare")}
                  </div>
                  <DeviceHoneycomb
                    mobile={mobile}
                    desktop={desktop}
                    isLightTheme={isLightTheme}
                    mobileLabel={t("websitePerformance.mobile")}
                    desktopLabel={t("websitePerformance.desktop")}
                  />
                  <StatList
                    items={[
                      {
                        key: "mobile",
                        color: MOBILE_COLOR,
                        label: t("websitePerformance.mobile"),
                        badge: formatPct(percentOf(mobile, deviceTotal)),
                        trend: asNumber(last7.mobile) >= asNumber(previous7.mobile) ? "up" : "down",
                        display: formatCount(mobile),
                      },
                      {
                        key: "desktop",
                        color: DESKTOP_COLOR,
                        label: t("websitePerformance.desktop"),
                        badge: formatPct(percentOf(desktop, deviceTotal)),
                        trend: asNumber(last7.desktop) >= asNumber(previous7.desktop) ? "up" : "down",
                        display: formatCount(desktop),
                      },
                    ]}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default WebsitePerformance;
